import React from 'react';
import { Mark } from '../Logo';
import Barras from './Barras';
import {
  AREAS_DO_PAINEL,
  AREAS_EM_ORDEM,
  EXECUCAO,
  NOTA,
  PESOS,
  REGISTRO,
  ROTULO_DA_AREA,
  decimalEm,
  perdaDe
} from './amostra';
import { useIdioma } from '../../i18n';
import { useFormato } from '../../i18n/formato';

const ARCO = 235.62; // 270° de um círculo de raio 50: o medidor é aberto embaixo
const TOM_DO_STATUS = (s) => (s >= 400 ? 'nh-crit' : s >= 300 ? 'nh-warn' : 'nh-ok');

const LINHAS = [
  { caminho: '/categoria/lancamentos', chave: 'inicio.tela.semMeta', tom: 'nh-crit' },
  { caminho: '/produtos/tenis-corrida-pro-x', chave: 'inicio.tela.tituloLongo', n: 71, tom: 'nh-warn' },
  { caminho: '/produtos/meia-compressao', chave: 'inicio.tela.semH1', tom: 'nh-crit' },
  { caminho: '/categoria/trilha?cor=azul', chave: 'inicio.tela.semMeta', tom: 'nh-crit' }
];

/**
 * A tela de uma execução, em três momentos: escopo, rastreando e concluída.
 * Os três ficam montados e se revezam pela opacidade; `estado` escolhe qual
 * aparece. É uma imagem do produto, então leitor de tela recebe a descrição
 * em `aria-label`, e não os rótulos soltos de cada estado.
 */
const TelaDaExecucao = ({ estado }) => {
  const { t } = useIdioma();
  const { numero, data, locale } = useFormato();
  const decimal = decimalEm(locale);
  const segundos = decimalEm(locale, 1);
  const total = numero(EXECUCAO.total);
  const perdas = Object.fromEntries(
    AREAS_EM_ORDEM.map((area) => [area, perdaDe(area, EXECUCAO.afetadas[area])])
  );

  return (
    <div
      className="tela"
      data-ui
      data-estado={estado}
      role="img"
      aria-label={t('inicio.tela.rotulo', { site: EXECUCAO.site, nota: NOTA, paginas: total })}
    >
      <div className="tela-topo">
        <Mark size={22} brackets={false} />
        <span className="tela-site">{EXECUCAO.site}</span>
        <span className="tela-tag">{t('inicio.tela.site')}</span>
        <span className="tela-meta">
          {t('inicio.tela.execucao', {
            n: EXECUCAO.numero,
            data: data(EXECUCAO.data, { day: 'numeric', month: 'short', year: 'numeric' })
          })}
        </span>
      </div>

      <div className="tela-estado tela-escopo" aria-hidden="true">
        <p className="nh-ui-titulo">{t('inicio.tela.enderecoInicial')}</p>
        <div className="tela-campo">
          <code>https://{EXECUCAO.site}</code>
        </div>
        <div className="tela-grupo">
          <p className="nh-ui-titulo">{t('inicio.tela.ateOnde')}</p>
          <div className="tela-opcao is-on"><i />{t('inicio.tela.seguirDominio')}</div>
          <div className="tela-opcao"><i />{t('inicio.tela.abaixoDoCaminho')}</div>
          <div className="tela-opcao"><i />{t('inicio.tela.incluirSubdominios')}</div>
        </div>
        <div className="tela-grupo">
          <p className="nh-ui-titulo">{t('inicio.tela.parametros')}</p>
          <div className="tela-opcao is-on"><i />{t('inicio.tela.descartar')}</div>
          <div className="tela-opcao"><i />{t('inicio.tela.manter')}</div>
        </div>
        <div className="tela-estimativa">
          <p>{t('inicio.tela.estimativa', { total: numero(EXECUCAO.sitemap), casam: total })}</p>
          <ul>
            {REGISTRO.filter(([status]) => status === 200)
              .slice(0, 3)
              .map(([, caminho]) => (
                <li key={caminho}><code>{caminho}</code></li>
              ))}
          </ul>
          <span className="tela-iniciar">{t('inicio.tela.iniciar')}</span>
        </div>
      </div>

      <div className="tela-estado tela-rastreando" aria-hidden="true">
        <p className="nh-ui-titulo">{t('inicio.tela.rastreando')}</p>
        <div className="tela-progresso">
          {numero(EXECUCAO.rastreadas)} <span>{t('inicio.tela.deTotal', { total })}</span>
        </div>
        <div className="tela-medidor">
          <i style={{ width: `${Math.round((EXECUCAO.rastreadas / EXECUCAO.total) * 100)}%` }} />
        </div>
        <div className="tela-fatos">
          <div><b>{t('inicio.tela.headless')}</b>{t('inicio.tela.javascript')}</div>
          <div>
            <b>{t('inicio.tela.robots', { n: numero(EXECUCAO.fechadasPeloRobots) })}</b>
            {t('inicio.tela.robotsTexto')}
          </div>
        </div>
        <div className="tela-registro">
          {/* a lista vai duas vezes para o rolamento dar a volta sem salto */}
          <ul>
            {[...REGISTRO, ...REGISTRO].map(([status, caminho, s], i) => (
              <li key={i}>
                <span className={TOM_DO_STATUS(status)}>{status}</span>
                <code>{caminho}</code>
                <span className={s >= 3 ? 'nh-warn' : undefined}>{segundos(s)} s</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="tela-estado tela-pronta" aria-hidden="true">
        <div className="tela-cabeca">
          <svg className="tela-medidor-nota" viewBox="0 0 120 120" aria-hidden="true">
            <circle cx="60" cy="60" r="50" fill="none" stroke="var(--sp-line-2)" strokeWidth="9"
              strokeDasharray={`${ARCO} 314.2`} transform="rotate(135 60 60)" />
            <circle cx="60" cy="60" r="50" fill="none" stroke="var(--sp-accent)" strokeWidth="9"
              strokeDasharray={`${(ARCO * NOTA) / 100} 314.2`} transform="rotate(135 60 60)" />
          </svg>
          <div className="nh-nota">
            <b>{NOTA}</b>
            <span>
              {t('inicio.tela.deCem')} · {t('otim.paginas', { n: total, quantidade: EXECUCAO.total })}
            </span>
            <em className="tela-desde">
              {t('inicio.tela.desde', { n: NOTA - EXECUCAO.anterior, anterior: EXECUCAO.numero - 1 })}
            </em>
          </div>
        </div>
        <div className="tela-grafico">
          <p className="nh-ui-titulo">{t('inicio.tela.pontosPerdidos')}</p>
          <Barras
            areas={AREAS_EM_ORDEM}
            pesos={PESOS}
            perdas={perdas}
            rotulo={(area) => t(ROTULO_DA_AREA[area])}
            decimal={decimal}
          />
        </div>
        <div className="tela-abas">
          {AREAS_DO_PAINEL.map((area) => (
            <span key={area} className={area === 'conteudo' ? 'is-on' : undefined}>
              {t(ROTULO_DA_AREA[area])}
              <span className="nh-num">{numero(EXECUCAO.afetadas[area])}</span>
            </span>
          ))}
        </div>
        <ul className="tela-linhas">
          {LINHAS.map((l) => (
            <li key={l.caminho}>
              <span className={`nh-ponto ${l.tom}`} />
              <code>{l.caminho}</code>
              <span className={l.tom}>{t(l.chave, { n: l.n })}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default TelaDaExecucao;
